import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, Truck } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  colhedora: "Colhedora", trator: "Trator", transbordo: "Transbordo",
  caminhao: "Caminhão", pa_carregadeira: "Pá Carregadeira",
  empilhadeira: "Empilhadeira", caldeira: "Caldeira", moenda: "Moenda",
  esteira: "Esteira", gerador: "Gerador",
  maquina_industrial: "Máquina Industrial",
  equipamento_agricola: "Equipamento Agrícola", outros: "Outros",
};

const empty = {
  codigo: "", nome: "", tipo: "outros", placa: "",
  sector_id: "", centro_custo: "", company_id: "",
  status: "ativo", observacoes: "",
};

export default function EquipmentsPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("master", "rh_admin", "rh_operational");
  const canDelete = hasRole("master", "rh_admin");
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const [e, c, s] = await Promise.all([
        api.get("/equipments"),
        api.get("/companies"),
        api.get("/sectors"),
      ]);
      setItems(e.data.items || []);
      setTypes(e.data.types || []);
      setCompanies(c.data || []);
      setSectors(s.data || []);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((e) => {
      if (tipoFilter !== "all" && e.tipo !== tipoFilter) return false;
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (!q) return true;
      return [e.codigo, e.nome, e.placa].join(" ").toLowerCase().includes(q);
    });
  }, [items, search, tipoFilter, statusFilter]);

  const submit = async () => {
    if (!form.codigo.trim() || !form.nome.trim()) {
      toast.error("Informe código e nome.");
      return;
    }
    try {
      if (editing) await api.put(`/equipments/${editing.id}`, form);
      else await api.post("/equipments", form);
      toast.success(editing ? "Equipamento atualizado" : "Equipamento criado");
      setOpen(false);
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const del = async (e) => {
    if (!window.confirm(`Inativar equipamento "${e.nome}"?`)) return;
    try {
      await api.delete(`/equipments/${e.id}`);
      toast.success("Equipamento inativado");
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="equipments-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Cadastros · Operacional</div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <Truck className="h-7 w-7 text-[#234935]" /> Equipamentos
          </h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground max-w-3xl">
            Cadastre frotas, máquinas industriais e equipamentos agrícolas para análise de custo de horas extras por equipamento.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
                  className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="new-equipment-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo equipamento
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Buscar código, nome ou placa..." className="pl-9" />
        </div>
        <Select value={tipoFilter} onValueChange={setTipoFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos tipos</SelectItem>
            {types.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t] || t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="ativo">Ativos</SelectItem>
            <SelectItem value="inativo">Inativos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Código</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Placa</TableHead>
              <TableHead>Setor</TableHead>
              <TableHead>Centro de custo</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Nenhum equipamento cadastrado</TableCell></TableRow>
            ) : filtered.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-[12px]">{e.codigo}</TableCell>
                <TableCell className="font-medium">{e.nome}</TableCell>
                <TableCell className="text-[12.5px] capitalize">{TYPE_LABELS[e.tipo] || e.tipo}</TableCell>
                <TableCell className="font-mono text-[12px]">{e.placa || "—"}</TableCell>
                <TableCell className="text-[12.5px] text-muted-foreground">
                  {sectors.find((s) => s.id === e.sector_id)?.name || "—"}
                </TableCell>
                <TableCell className="text-[12.5px] text-muted-foreground">{e.centro_custo || "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={e.status === "ativo" ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-slate-100 text-slate-700"}>
                    {e.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {canEdit && (
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(e); setForm({ ...empty, ...e }); setOpen(true); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button size="icon" variant="ghost" onClick={() => del(e)}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar equipamento" : "Novo equipamento"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Código</Label>
              <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} className="font-mono uppercase" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Nome</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t] || t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Placa</Label>
              <Input value={form.placa || ""} onChange={(e) => setForm({ ...form, placa: e.target.value })} className="font-mono uppercase" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Centro de custo</Label>
              <Input value={form.centro_custo || ""} onChange={(e) => setForm({ ...form, centro_custo: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Empresa</Label>
              <Select value={form.company_id || "none"} onValueChange={(v) => setForm({ ...form, company_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Sem empresa" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Setor</Label>
              <Select value={form.sector_id || "none"} onValueChange={(v) => setForm({ ...form, sector_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Sem setor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="text-[11px] uppercase tracking-wider">Observações</Label>
              <Textarea rows={2} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="save-equipment">
              {editing ? "Salvar alterações" : "Criar equipamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
